import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ehAcessoNegado, ehSessaoExpirada } from '@core/errors';
import { AuthService } from '@modules/auth/services/auth.service';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { TokenService } from '../services/token.service';
import { PULAR_REFRESH } from './pular-refresh.context';

const isRefreshing = { value: false };
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(
  null,
);

/** Rotas públicas: não recebem token nem participam do fluxo de refresh. */
const ROTAS_PUBLICAS = [
  '/autenticacao/login',
  '/autenticacao/register',
  '/autenticacao/recuperar-senha/solicitar',
  '/autenticacao/recuperar-senha/verificar',
  '/autenticacao/recuperar-senha/redefinir',
];

/**
 * Página do link do convite (#825): `/api/v1/convites/:token/...`. A credencial é o token do link, e
 * quem abre pode estar logado em outra conta: o token da sessão não vai junto (o backend montaria o
 * contexto da empresa logada) e nenhum erro dali mexe na sessão. Casa só o recurso na raiz da API,
 * para não pegar `/usuarios/convites` (o admin) nem `/usuarios/meus-convites` (o aviso), que exigem login.
 */
const ROTA_PUBLICA_CONVITE = /\/api\/v1\/convites\//;

function ehRotaPublica(url: string): boolean {
  return ROTAS_PUBLICAS.some((rota) => url.includes(rota)) || ROTA_PUBLICA_CONVITE.test(url);
}

/**
 * Anexa o access token e, **somente em 401**, tenta renovar a sessão.
 *
 * Regras (espelham o contrato de erros da API):
 * - **401** = sessão/token inválido ou expirado → único caso que tenta refresh
 *   e, se o refresh falhar, desloga.
 * - **403 sem `codigo`** = o guard de permissão do backend barrou. Só manda para
 *   /acesso-negado quando é **GET** — carregamento de tela, o caso de quem chegou
 *   a uma tela que não podia abrir. Em POST/PUT/PATCH/DELETE o 403 é uma AÇÃO
 *   negada (trocar a logo, publicar, excluir): a tela precisa continuar de pé
 *   para exibir o aviso, senão o usuário perde o que já preencheu.
 * - **400 / 409 / 422 / 403 COM `codigo`** = erro de negócio (senha errada ao
 *   assinar, assinante não elegível, bloco já assinado) → apenas propaga, para a
 *   própria tela exibir. Não confundir com o de cima: expulsar o usuário daqui
 *   quebraria o fluxo de assinatura.
 * - Requisições marcadas com `PULAR_REFRESH` (reautenticação) nunca disparam
 *   refresh/logout, mesmo em 401 — e nunca são reenviadas.
 */
export const autenticacaoInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const tokenService = inject(TokenService);
  const authService = inject(AuthService);
  const router = inject(Router);

  if (ehRotaPublica(request.url)) {
    return next(request);
  }

  const accessToken = tokenService.getAccessToken();
  const requisicao = accessToken ? adicionarToken(request, accessToken) : request;

  return next(requisicao).pipe(
    catchError((erro: unknown) => {
      const ehRotaDeRefresh = requisicao.url.includes('/autenticacao/refresh');
      const pularRefresh = requisicao.context.get(PULAR_REFRESH);

      // O próprio refresh falhou: não há como renovar a sessão. Desloga apenas
      // em 401 (refresh token inválido/expirado); demais falhas só propagam.
      if (ehRotaDeRefresh) {
        if (ehSessaoExpirada(erro)) {
          isRefreshing.value = false;
          authService.deslogar();
        }
        return throwError(() => erro);
      }

      // Único caminho que renova a sessão: 401 fora de rota de reautenticação.
      if (ehSessaoExpirada(erro) && !pularRefresh) {
        return tratarSessaoExpirada(requisicao, next, authService);
      }

      // 403 do guard de permissão (sem código de domínio) em GET: o usuário chegou
      // onde não podia — troca a tela por /acesso-negado. Numa mutação, apenas
      // propaga: a tela que disparou a ação mostra o aviso sem perder o contexto.
      if (ehAcessoNegado(erro) && requisicao.method === 'GET') {
        void router.navigate(['/acesso-negado']);
        return throwError(() => erro);
      }

      // 400/409/422/5xx, 403 de negócio e 401 de reautenticação: propaga.
      return throwError(() => erro);
    }),
  );
};

function adicionarToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function tratarSessaoExpirada(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing.value) {
    isRefreshing.value = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((response) => {
        isRefreshing.value = false;
        refreshTokenSubject.next(response.accessToken);
        return next(adicionarToken(request, response.accessToken));
      }),
      catchError((erro: unknown) => {
        isRefreshing.value = false;
        console.error('[AutenticacaoInterceptor] Falha ao renovar o token; deslogando:', erro);
        authService.deslogar();
        return throwError(() => erro);
      }),
    );
  }

  // Refresh em andamento: aguarda o novo token e reenvia a requisição.
  return refreshTokenSubject.pipe(
    filter((token): token is string => token !== null),
    take(1),
    switchMap((token) => next(adicionarToken(request, token))),
  );
}
